/* Bindings to work with UI state that persists over
 * browser sessions.
 *
 * Two backing stores: for the Designer, we save the state
 * in the database by interfacing with the API. For the Editor,
 * we save the data in local storage.
 */
import _ from 'lodash';
import {reportBug, reportException} from '@packages/utilities/reporter';
import type {
  ValidationErrors,
  PersistentUIState,
} from '@packages/shared/types/persistentUIState';
import webflowFetch from '@packages/utilities/fetch';

export const PERSISTENT_UI_STATE_LOCALSTORAGE_KEY = 'webflowPersistentUIState';

const safeSetLocaleStorage = (state: PersistentUIState) => {
  try {
    window.localStorage.setItem(
      PERSISTENT_UI_STATE_LOCALSTORAGE_KEY,
      JSON.stringify(state)
    );
  } catch (e) {
    // ignore localStorage not existing or incognito mode
  }
};

const safeGetLocalStorageItem = () => {
  try {
    const stateJson = window.localStorage.getItem(
      PERSISTENT_UI_STATE_LOCALSTORAGE_KEY
    );

    if (stateJson) {
      return JSON.parse(stateJson);
    }
  } catch (err) {
    // ignore localStorage not existing or incognito mode
  }

  return {};
};

interface IPersistentUIStateStorage {
  /**
   * Stores state in the backend for a given site. More information
   * in packages/systems/designer/persistent-state/README.md.
   */
  put(siteName: string, state: PersistentUIState): Promise<PersistentUIState>;

  /**
   * Retrieves state from the backend for a given site. More information
   * in packages/systems/designer/persistent-state/README.md.
   */
  get(siteName: string): Promise<PersistentUIState>;
}

export const BackendPersistentUIStateStorage: IPersistentUIStateStorage = {
  async put(siteName: string, state: PersistentUIState) {
    try {
      const resp = await webflowFetch(`/api/sites/${siteName}/uistate`, {
        method: 'PUT',
        body: JSON.stringify(state),
      });

      if (!resp.ok) {
        throw new Error(
          `Saving persistent UI state failed with status ${resp.status}`
        );
      }

      const {
        state: newState,
        errors,
      }: {
        state: PersistentUIState;
        errors: ValidationErrors;
      } = await resp.json();
      if (Object.keys(errors || {}).length > 0) {
        console.warn(
          '[PersistentUIState] invalid keys ignored when saving UI state:',
          errors
        );
      }
      return newState;
    } catch (error) {
      if (error instanceof Error) {
        reportException(
          error,
          {team: 'design-experience'},
          {skipConsole: true}
        );
      }
      return state;
    }
  },
  async get(siteName: string) {
    const resp = await webflowFetch(`/api/sites/${siteName}/uistate`, {
      method: 'GET',
    });

    return await resp.json();
  },
};

const LocalStoragePersistentUIStateStorage: IPersistentUIStateStorage = {
  async put(_siteName: string, state: PersistentUIState) {
    safeSetLocaleStorage(state);
    return state;
  },
  async get(_siteName: string) {
    return Promise.resolve(safeGetLocalStorageItem());
  },
};

let _clientCache: null | {
  client: IPersistentUIStateStorage;
  siteName: string;
  state: PersistentUIState;
  debouncedPut: ((
    siteName: string,
    state: PersistentUIState
  ) => Promise<PersistentUIState>) & {cancel?: () => void};
} = null;

function _logNotInitialized() {
  if (process.env.NODE_ENV !== 'test') {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug(
      'PersistentUIStateInitializationError',
      'Tried to use PersistentUIState before it was initialized'
    );
  }
}

export function replace(state: PersistentUIState): void {
  if (!_clientCache) {
    _logNotInitialized();
    return;
  }
  _clientCache.state = state;
}

export async function syncToBackend(forceNow?: boolean | null): Promise<void> {
  if (!_clientCache) {
    _logNotInitialized();
    return;
  }
  if (forceNow) {
    // Cancel any pending trailing-edge debounced put before issuing the
    // immediate write. Lodash debounce queues the arg snapshot from the most
    // recent debouncedPut invocation; that snapshot predates the forceNow
    // merge that just updated `_clientCache.state`, so letting it fire would
    // overwrite the backend with stale state.
    _clientCache.debouncedPut.cancel?.();
    await _clientCache.client.put(_clientCache.siteName, _clientCache.state);
    return;
  }
  await _clientCache.debouncedPut(_clientCache.siteName, _clientCache.state);
}

/**
 * Merges a given persistent state object into the current state and saves it to the backend.
 *
 * @param mergeObj The object to merge into the current state.
 * @param options.forceNow If true, the state will be saved immediately instead of debounced.
 */
export async function merge(
  mergeObj: PersistentUIState,
  options: {
    forceNow: boolean;
  } = {
    forceNow: false,
  }
): Promise<void> {
  if (!_clientCache) {
    _logNotInitialized();
    return;
  }
  const prevState = get();

  _clientCache.state = {
    ..._clientCache.state,
    ...mergeObj,
  };

  for (const key in mergeObj) {
    const listeners = OBSERVE.get(key);

    if (listeners) {
      const typedKey = key as keyof PersistentUIState;
      const prevValue = prevState[typedKey];
      const nextValue = get()[typedKey];
      for (const listener of listeners) {
        listener(nextValue, prevValue);
      }
    }
  }

  await syncToBackend(options.forceNow);
}

type Listener = <V>(nextValue: V, prevValue: V) => void;
const OBSERVE = new Map();

/**
 * Observe changes to a specific key in the persistent state.
 *
 * @param key The key to observe.
 * @param listener The callback function to be called when the key changes.
 */
export function observeKey<Key extends keyof PersistentUIState>(
  key: Key,
  listener: Listener
) {
  OBSERVE.set(key, (OBSERVE.get(key) || new Set()).add(listener));
}

/**
 * Stop observing changes to a specific key in the persistent state.
 *
 * @param key The key to stop observing.
 * @param listener The callback function to be removed from the observers.
 */
export function unobserveKey<Key extends keyof PersistentUIState>(
  key: Key,
  listener: Listener
) {
  (OBSERVE.get(key) || new Set()).delete(listener);
}

export const UISTATE_DEBOUNCE_WAIT = 5000;

// In the designer, we want an explicit initialize call once we've gotten
// the initial state from the backend: otherwise modules could access the state
// before we've loaded it.
export function initializeToBackend(
  siteName: string,
  state: PersistentUIState
) {
  _clientCache = {
    client: BackendPersistentUIStateStorage,
    siteName,
    state,
    // Debounce logic:
    // A = persistentUI.put() call
    // B = debounce waiting counter
    // C = actual HTTP PUT request
    //
    // A  put()----put()---put()------------
    //    |        |       |
    // B  <----wait|       |
    //    |        <---wait|
    //    |                <----wait---->|
    //    |                              |
    //    |                              |
    // C  PUT----------------------------PUT
    //
    // This has been updated to 5s because on designer load each site
    // sends off multiple put() calls in quick succession.
    // Previous value was 3s and it resulted in 5 requests on designer load
    // which caused 504 errors during high load times.
    debouncedPut: _.debounce(
      BackendPersistentUIStateStorage.put,
      UISTATE_DEBOUNCE_WAIT, // wait for another put for 5s
      {
        leading: true,
        trailing: true,
        // if requests continue being made there's a chance they'll continue to be debounced and never run
        // maxWait ensures requests are still sent
        maxWait: UISTATE_DEBOUNCE_WAIT,
      }
    ),
  };
}

export function initializeToLocalStorage(state: PersistentUIState) {
  _clientCache = {
    client: LocalStoragePersistentUIStateStorage,
    siteName: 'ignored-by-editor-client',
    state,
    // We don't need debouncing for local storage
    // since it's a cheap sync operation
    debouncedPut: LocalStoragePersistentUIStateStorage.put,
  };
}

export function get(): Partial<PersistentUIState> {
  if (!_clientCache) {
    _logNotInitialized();
    return {};
  }
  return _clientCache.state;
}
